import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Calendar, ArrowRight } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NewsPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    const fetchArticles = async () => {
      setLoading(true);
      try {
        const url =
          filter === "all"
            ? `${API}/articles`
            : `${API}/articles?category=${filter}`;
        const response = await axios.get(url);
        setArticles(response.data);
      } catch (error) {
        console.error("Error fetching articles:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, [filter]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("it-IT", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const getCategoryLabel = (category) => {
    switch (category) {
      case "partite":
        return "Partite";
      case "comunicati":
        return "Comunicato Ufficiale";
      case "scuola_calcio":
        return "Scuola Calcio";
      default:
        return "News";
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case "partite":
        return "bg-green-100 text-green-700";
      case "comunicati":
        return "bg-amber-100 text-amber-700";
      case "scuola_calcio":
        return "bg-purple-100 text-purple-700";
      default:
        return "bg-blue-100 text-[#0047AB]";
    }
  };

  return (
    <div data-testid="news-page">
      <Navbar />

      {/* Hero */}
      <section className="pt-24 lg:pt-32 pb-12 bg-[#0047AB] noise-overlay">
        <div className="max-w-7xl mx-auto px-4 text-center text-white">
          <h1 className="text-4xl lg:text-5xl font-black uppercase mb-4 animate-fade-in-up">
            News
          </h1>
          <p className="text-blue-200 max-w-xl mx-auto">
            Tutte le ultime notizie dal mondo Volpiano Calcio
          </p>
        </div>
      </section>

      {/* Filter */}
      <section className="py-6 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { id: "all", label: "Tutte" },
              { id: "news", label: "News" },
              { id: "partite", label: "Partite" },
              { id: "comunicati", label: "Comunicati Ufficiali" },
              { id: "scuola_calcio", label: "Programma Scuola Calcio" },
            ].map((cat) => (
              <button
                key={cat.id}
                data-testid={`filter-${cat.id}`}
                onClick={() => setFilter(cat.id)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                  filter === cat.id
                    ? "bg-[#0047AB] text-white"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="section-padding bg-slate-50" data-testid="articles-grid">
        <div className="max-w-7xl mx-auto px-4">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div
                  key={i}
                  className="animate-pulse bg-white rounded-xl overflow-hidden"
                >
                  <div className="h-48 bg-slate-200" />
                  <div className="p-6 space-y-3">
                    <div className="h-4 bg-slate-200 rounded w-1/4" />
                    <div className="h-5 bg-slate-200 rounded w-3/4" />
                    <div className="h-3 bg-slate-200 rounded w-full" />
                    <div className="h-3 bg-slate-200 rounded w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : articles.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map((article, index) => (
                <Link
                  key={article.id}
                  to={`/news/${article.id}`}
                  data-testid={`article-card-${article.id}`}
                  className={`card-hover bg-white rounded-xl overflow-hidden shadow-sm group ${
                    index === 0 ? "lg:col-span-2 lg:row-span-2" : ""
                  }`}
                >
                  <div
                    className={`overflow-hidden bg-slate-100 ${
                      index === 0 ? "aspect-[4/3] lg:aspect-[16/10]" : "aspect-[4/3]"
                    }`}
                  >
                    <img
                      src={
                        article.image_url ||
                        "https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=800"
                      }
                      alt={article.title}
                      className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className={`p-6 ${index === 0 ? "lg:p-8" : ""}`}>
                    <div className="flex items-center gap-3 mb-3">
                      <span
                        className={`text-xs font-semibold px-3 py-1 rounded-full ${getCategoryColor(
                          article.category
                        )}`}
                      >
                        {getCategoryLabel(article.category)}
                      </span>
                      <span className="text-xs text-slate-500 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(article.published_date || article.created_at)}
                      </span>
                      {article.author && (
                        <span className="text-xs text-slate-500">
                          di <span className="font-medium">{article.author}</span>
                        </span>
                      )}
                    </div>
                    <h3
                      className={`font-bold text-slate-900 mb-2 group-hover:text-[#0047AB] transition-colors line-clamp-2 ${
                        index === 0 ? "text-2xl lg:text-3xl" : "text-lg"
                      }`}
                    >
                      {article.title}
                    </h3>
                    <p
                      className={`text-slate-600 mb-4 ${
                        index === 0 ? "line-clamp-3" : "line-clamp-2"
                      } text-sm`}
                    >
                      {article.excerpt}
                    </p>
                    <span className="inline-flex items-center gap-1 text-[#0047AB] font-medium text-sm group-hover:gap-2 transition-all">
                      Leggi di più <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-slate-500 text-lg">
                Nessun articolo trovato per questa categoria
              </p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default NewsPage;
